"use client";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Play, Zap } from "lucide-react";

export default function WorkflowTestModal({ open, onClose, onExecute }) {
  const [inputParams, setInputParams] = useState("");
  const [error, setError] = useState("");

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleExecute = (e) => {
    e.preventDefault();
    setError("");

    let params = {};
    
    if (inputParams.trim()) {
      try {
        params = JSON.parse(inputParams);
      } catch (err) {
        setError("Invalid JSON format");
        return;
      }
    }

    onExecute(params);
    setInputParams("");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdrop}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="w-full max-w-2xl rounded-2xl bg-black border border-white/10 shadow-2xl"
          >
        <div className="border-b border-white/10 px-8 py-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-600/20 border border-violet-500/30">
                <Zap className="h-6 w-6 text-violet-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Workflow Test Mode</h2>
                <p className="mt-1.5 text-sm text-gray-400">
                  Run the live draft of the workflow as a test execution
                </p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/5 p-1"
            >
              <X size={22} />
            </button>
          </div>
        </div>

        <form onSubmit={handleExecute} className="px-8 py-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Input Parameters <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <textarea
              value={inputParams}
              onChange={(e) => {
                setInputParams(e.target.value);
                setError("");
              }}
              rows={10}
              className="w-full resize-none rounded-xl bg-zinc-950 border border-white/10 px-4 py-3 text-sm text-white placeholder-gray-600 font-mono leading-relaxed focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 transition-all"
              placeholder='{\n  "example_key": "example_value",\n  "user_id": "12345"\n}'
            />
            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2">
                <span className="text-red-400 text-xs font-medium">{error}</span>
              </div>
            )}
            <p className="text-xs text-gray-500 leading-relaxed">
              Provide JSON input parameters or leave empty to run with default values. 
              The workflow will execute against real APIs using the draft configuration.
            </p>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-white/10 bg-white/5 px-6 py-2.5 text-sm font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-violet-700 transition-all shadow-lg shadow-violet-600/20"
            >
              <Play size={16} />
              Run Test
            </button>
          </div>
        </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
